import { useState, useEffect } from "react";
import { X } from "lucide-react";
import { EventType } from "@/types/event-type.enum";
import { EventPriority } from "@/types/event-priority.enum";

type FormData = {
  title: string;
  description?: string;
  startDate: string;
  endDate?: string;
  type: string;
  priority: string;
  isActive: boolean;
  isGlobal: boolean;
  metadata?: string;
};

export default function EventForm({
  isOpen,
  onClose,
  onSubmit,
  initialData,
  isSubmitting,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: any) => Promise<void>;
  initialData?: any;
  isSubmitting?: boolean;
}) {
  const [form, setForm] = useState<FormData>({
    title: "",
    description: "",
    startDate: "",
    endDate: "",
    type: EventType.GENERAL,
    priority: EventPriority.MEDIUM,
    isActive: true,
    isGlobal: true,
    metadata: "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (initialData) {
      setForm({
        title: initialData.title || "",
        description: initialData.description || "",
        startDate: initialData.startDate
          ? new Date(initialData.startDate).toISOString().slice(0, 16)
          : "",
        endDate: initialData.endDate
          ? new Date(initialData.endDate).toISOString().slice(0, 16)
          : "",
        type: initialData.type || EventType.GENERAL,
        priority: initialData.priority || EventPriority.MEDIUM,
        isActive: initialData.isActive ?? true,
        isGlobal: initialData.isGlobal ?? true,
        metadata: initialData.metadata
          ? JSON.stringify(initialData.metadata, null, 2)
          : "",
      });
    } else {
      // reset
      setForm((f) => ({
        ...f,
        title: "",
        description: "",
        startDate: "",
        endDate: "",
        metadata: "",
      }));
    }
    setErrors({});
  }, [initialData, isOpen]);

  if (!isOpen) return null;

  const validate = (): boolean => {
    const e: Record<string, string> = {};
    if (!form.title || form.title.trim().length === 0)
      e.title = "Title is required";
    if (!form.startDate) e.startDate = "Start date is required";
    if (
      form.endDate &&
      form.startDate &&
      new Date(form.endDate) < new Date(form.startDate)
    )
      e.endDate = "End date must be after start date";
    if (form.metadata) {
      try {
        JSON.parse(form.metadata);
      } catch (err) {
        e.metadata = "Invalid JSON";
      }
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    const payload: any = {
      title: form.title,
      description: form.description || undefined,
      startDate: new Date(form.startDate).toISOString(),
      endDate: form.endDate ? new Date(form.endDate).toISOString() : undefined,
      type: form.type,
      priority: form.priority,
      isActive: form.isActive,
      isGlobal: form.isGlobal,
      metadata: form.metadata ? JSON.parse(form.metadata) : undefined,
    };
    await onSubmit(payload);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose} />
      <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-6 pb-4 border-b border-gray-100 shrink-0">
          <h3 className="text-lg font-semibold">
            {initialData ? "Edit Event" : "Create Event"}
          </h3>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-full"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        <div className="overflow-y-auto p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm">Title *</label>
            <input
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="w-full border px-2 py-1 rounded"
            />
            {errors.title && (
              <div className="text-sm text-red-600">{errors.title}</div>
            )}
          </div>

          <div>
            <label className="block text-sm">Type</label>
            <select
              value={form.type}
              onChange={(e) => setForm({ ...form, type: e.target.value })}
              className="w-full border px-2 py-1 rounded"
            >
              {(Object.values(EventType) as string[]).map((v) => (
                <option key={v} value={v}>
                  {v === "college_event" ? "institution event" : v.replace("_", " ")}
                </option>
              ))}
            </select>
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm">Description</label>
            <textarea
              value={form.description}
              onChange={(e) =>
                setForm({ ...form, description: e.target.value })
              }
              className="w-full border px-2 py-1 rounded"
              rows={3}
            />
          </div>

          <div>
            <label className="block text-sm">Start Date *</label>
            <input
              type="datetime-local"
              value={form.startDate}
              onChange={(e) => setForm({ ...form, startDate: e.target.value })}
              className="w-full border px-2 py-1 rounded"
            />
            {errors.startDate && (
              <div className="text-sm text-red-600">{errors.startDate}</div>
            )}
          </div>

          <div>
            <label className="block text-sm">End Date</label>
            <input
              type="datetime-local"
              value={form.endDate}
              onChange={(e) => setForm({ ...form, endDate: e.target.value })}
              className="w-full border px-2 py-1 rounded"
            />
            {errors.endDate && (
              <div className="text-sm text-red-600">{errors.endDate}</div>
            )}
          </div>

          <div>
            <label className="block text-sm">Priority</label>
            <select
              value={form.priority}
              onChange={(e) => setForm({ ...form, priority: e.target.value })}
              className="w-full border px-2 py-1 rounded"
            >
              {(Object.values(EventPriority) as string[]).map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col">
            <label className="block text-sm">Flags</label>
            <div className="flex items-center gap-4 mt-1">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={form.isActive}
                  onChange={(e) =>
                    setForm({ ...form, isActive: e.target.checked })
                  }
                />{" "}
                Active
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={form.isGlobal}
                  onChange={(e) =>
                    setForm({ ...form, isGlobal: e.target.checked })
                  }
                />{" "}
                Global
              </label>
            </div>
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm">Metadata (JSON)</label>
            <textarea
              value={form.metadata}
              onChange={(e) => setForm({ ...form, metadata: e.target.value })}
              className="w-full border px-2 py-1 rounded font-mono text-sm"
              rows={4}
            />
            {errors.metadata && (
              <div className="text-sm text-red-600">{errors.metadata}</div>
            )}
          </div>
        </div>
        </div>

        <div className="flex items-center justify-end gap-3 p-6 pt-4 border-t border-gray-100 shrink-0">
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50"
          >
            {isSubmitting
              ? "Saving…"
              : initialData
              ? "Update Event"
              : "Create Event"}
          </button>
        </div>
      </div>
    </div>
  );
}
